import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileText, ExternalLink, ChevronRight, Loader2, Image, Video, Music, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import AppShell from '@/components/AppShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const gradeLabels = {
  7: 'Kansas History', 8: 'U.S. History', 9: 'World History',
  10: 'Modern World', 11: 'U.S. History', 12: 'Government & Economics'
};

const SOURCE_ICONS = {
  document: FileText, image: Image, video: Video, audio: Music,
  artifact: FileText, website: ExternalLink
};

const SOURCE_COLORS = {
  document: 'bg-blue-100 text-blue-700',
  image: 'bg-green-100 text-green-700',
  video: 'bg-purple-100 text-purple-700',
  audio: 'bg-orange-100 text-orange-700',
  artifact: 'bg-yellow-100 text-yellow-700',
  website: 'bg-slate-100 text-slate-700'
};

export default function PrimarySourcesIndex() {
  const navigate = useNavigate();
  const [unitsByGrade, setUnitsByGrade] = useState({});
  const [sourcesByUnit, setSourcesByUnit] = useState({});
  const [loadedGrades, setLoadedGrades] = useState(new Set());
  const [loadingGrade, setLoadingGrade] = useState(true);
  const [activeGrade, setActiveGrade] = useState(7);
  const [expandedUnit, setExpandedUnit] = useState(null);
  const [query, setQuery] = useState('');
  const trimmedQuery = query.trim().toLowerCase();
  const isSearching = trimmedQuery.length >= 2;

  // Load one grade at a time: fetch grade's units, then fetch all primary sources in parallel.
  const loadGrade = useCallback(async (grade) => {
    if (loadedGrades.has(grade)) return;
    setLoadingGrade(true);
    try {
      const unitsRes = await axios.get(`${API}/units/grade/${grade}`);
      const units = unitsRes.data || [];
      // Parallel fetch of primary sources for every unit
      const sourceResponses = await Promise.all(
        units.map(u =>
          axios.get(`${API}/primary-sources/${u.id}`)
            .then(r => [u.id, r.data?.sources || []])
            .catch(() => [u.id, []])
        )
      );
      const sourceMap = {};
      sourceResponses.forEach(([uid, srcs]) => {
        if (srcs.length > 0) sourceMap[uid] = srcs;
      });
      setUnitsByGrade(prev => ({ ...prev, [grade]: units }));
      setSourcesByUnit(prev => ({ ...prev, ...sourceMap }));
      setLoadedGrades(prev => new Set(prev).add(grade));
    } catch (_err) {
      setUnitsByGrade(prev => ({ ...prev, [grade]: [] }));
      setLoadedGrades(prev => new Set(prev).add(grade));
    } finally {
      setLoadingGrade(false);
    }
  }, [loadedGrades]);

  // Load the active grade whenever it changes
  useEffect(() => {
    loadGrade(activeGrade);
  }, [activeGrade, loadGrade]);

  // When searching, lazy-load every grade so results span the whole library.
  useEffect(() => {
    if (!isSearching) return;
    [7, 8, 9, 10, 11, 12].forEach(g => {
      if (!loadedGrades.has(g)) loadGrade(g);
    });
  }, [isSearching, loadedGrades, loadGrade]);

  const getSourceCount = (unitId) => (sourcesByUnit[unitId] || []).length;
  const totalForGrade = (grade) => (unitsByGrade[grade] || []).reduce((sum, u) => sum + getSourceCount(u.id), 0);

  // Search results: list of { grade, unit, sources, unitMatched } across all loaded grades.
  const searchResults = useMemo(() => {
    if (!isSearching) return [];
    const q = trimmedQuery;
    const matches = [];
    Object.entries(unitsByGrade).forEach(([gradeKey, units]) => {
      const grade = Number(gradeKey);
      (units || []).forEach(unit => {
        const unitMatched = (unit.title || '').toLowerCase().includes(q);
        const allSources = sourcesByUnit[unit.id] || [];
        const matchedSources = allSources.filter(s =>
          (s.title || '').toLowerCase().includes(q) ||
          (s.description || '').toLowerCase().includes(q)
        );
        // Include unit if its title matched (show all sources) OR if any source matched (show only matched sources)
        if (unitMatched) {
          matches.push({ grade, unit, sources: allSources, unitMatched: true });
        } else if (matchedSources.length > 0) {
          matches.push({ grade, unit, sources: matchedSources, unitMatched: false });
        }
      });
    });
    return matches;
  }, [isSearching, trimmedQuery, unitsByGrade, sourcesByUnit]);

  const matchedSourceCount = useMemo(
    () => searchResults.reduce((sum, r) => sum + r.sources.length, 0),
    [searchResults]
  );
  const allSearchGradesLoaded = isSearching && [7, 8, 9, 10, 11, 12].every(g => loadedGrades.has(g));

  if (loadingGrade && !loadedGrades.has(activeGrade)) return (
    <AppShell>
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-[#14213D]" />
        <p className="text-sm text-[#5A6577]">Loading Grade {activeGrade} primary sources…</p>
      </div>
    </AppShell>
  );

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 sm:py-10">
        <div className="mb-6">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-2">Resources</p>
          <h1 className="font-serif-display text-3xl sm:text-4xl text-[#14213D] tracking-tight mb-2">Primary Source Library</h1>
          <p className="text-sm text-[#5A6577] max-w-2xl">Annotated historical documents, images, and artifacts organized by grade and unit.</p>
        </div>

        {/* Search bar */}
        <div className="max-w-xl mb-6 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5A6577] pointer-events-none" />
          <Input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search 957 sources — try 'Mayflower', 'Hammurabi', 'Civil War'…"
            className="pl-10 pr-10 bg-white border-[#EBE7E0] focus-visible:ring-[#14213D]/30"
            data-testid="ps-search-input"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5A6577] hover:text-[#14213D]"
              aria-label="Clear search"
              data-testid="ps-search-clear"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          {isSearching && (
            <p className="text-xs text-[#5A6577] mt-2" data-testid="ps-search-status">
              {allSearchGradesLoaded ? (
                <>Found <strong>{searchResults.length}</strong> unit{searchResults.length !== 1 ? 's' : ''} · <strong>{matchedSourceCount}</strong> matching source{matchedSourceCount !== 1 ? 's' : ''} across all grades</>
              ) : (
                <span className="inline-flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Searching all grades… {searchResults.length} match{searchResults.length !== 1 ? 'es' : ''} so far</span>
              )}
            </p>
          )}
        </div>

        {/* Grade Tabs (hidden during search) */}
        {!isSearching && (
        <div className="flex flex-wrap gap-2 mb-8">
          {[7, 8, 9, 10, 11, 12].map(g => (
            <button key={g}
              onClick={() => { setActiveGrade(g); setExpandedUnit(null); }}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeGrade === g ? 'bg-[#14213D] text-[#FDFBF7]' : 'bg-white text-[#2D3748] hover:bg-[#F9F8F4] border border-[#EBE7E0]'}`}
              data-testid={`grade-tab-${g}`}
            >
              Grade {g}: {gradeLabels[g]}
              <span className="ml-1.5 text-xs opacity-70">
                {loadedGrades.has(g) ? `(${totalForGrade(g)} sources)` : ''}
              </span>
            </button>
          ))}
        </div>
        )}

        {/* Inline loader for grade switches */}
        {loadingGrade && loadedGrades.has(activeGrade) === false && (
          <div className="flex items-center justify-center gap-2 py-4 text-sm text-[#5A6577]" data-testid="grade-switch-loader">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading Grade {activeGrade}…
          </div>
        )}

        {/* Units with Sources — either search results OR active grade */}
        <div className="space-y-3">
          {isSearching && searchResults.length === 0 && allSearchGradesLoaded && (
            <div className="text-center py-12 text-sm text-[#5A6577]" data-testid="ps-no-results">
              No primary sources match &ldquo;<strong>{query}</strong>&rdquo;. Try a different keyword.
            </div>
          )}
          {(isSearching
            ? searchResults.map((r, i) => ({ unit: r.unit, sources: r.sources, grade: r.grade, forceExpand: true, idx: i }))
            : (unitsByGrade[activeGrade] || []).map((unit, i) => ({ unit, sources: sourcesByUnit[unit.id] || [], grade: activeGrade, forceExpand: false, idx: i }))
          ).map(({ unit, sources, grade, forceExpand, idx }) => {
            const isExpanded = forceExpand || expandedUnit === unit.id;
            return (
              <Card key={`${grade}-${unit.id}`} className="border border-[#EBE7E0] shadow-sm bg-white overflow-hidden" data-testid={`source-unit-${idx}`}>
                <button
                  className="w-full text-left p-4 flex items-center justify-between hover:bg-[#F9F8F4] transition-colors"
                  onClick={() => !forceExpand && setExpandedUnit(isExpanded ? null : unit.id)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-md bg-[#F9F8F4] border border-[#EBE7E0] flex items-center justify-center">
                      <span className="text-xs font-bold text-[#14213D]">{unit.unit_number}</span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-serif-display text-sm text-[#14213D]">{unit.title}</h3>
                        {isSearching && (
                          <Badge variant="outline" className="text-[9px] border-[#EBE7E0] text-[#7A93AC] bg-[#F9F8F4]">
                            Grade {grade}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-[#5A6577] mt-0.5">{sources.length} {isSearching ? 'matching ' : ''}primary source{sources.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[11px] border-[#EBE7E0]">{sources.length}</Badge>
                    {!forceExpand && (
                      <ChevronRight className={`h-4 w-4 text-[#5A6577] transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                    )}
                  </div>
                </button>

                {isExpanded && sources.length > 0 && (
                  <div className="border-t border-[#EBE7E0] p-4 bg-[#F9F8F4]/40">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {sources.map((src, j) => {
                        const Icon = SOURCE_ICONS[src.type] || FileText;
                        const colorClass = SOURCE_COLORS[src.type] || 'bg-[#E2E8F0] text-[#2D3748]';
                        return (
                          <div key={`${unit.id}-${j}`} className="flex gap-3 p-3 bg-white rounded-md border border-[#EBE7E0]" data-testid={`source-item-${j}`}>
                            <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                              <Icon className="h-4 w-4" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <h4 className="text-xs font-semibold text-[#14213D] truncate">{src.title}</h4>
                              <p className="text-[11px] text-[#5A6577] mt-0.5 line-clamp-2">{src.description}</p>
                              {src.url && (
                                <a href={src.url} target="_blank" rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-[11px] text-[#14213D] hover:text-[#D4AF37] mt-1">
                                  <ExternalLink className="h-2.5 w-2.5" /> View Source
                                </a>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-3 text-right">
                      <Button variant="outline" size="sm" className="text-xs border-[#14213D]/30 text-[#14213D]" onClick={() => navigate(`/primary-sources/${unit.id}`)}>
                        View All & Annotate <ChevronRight className="h-3 w-3 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}

                {isExpanded && sources.length === 0 && (
                  <div className="border-t border-[#EBE7E0] p-6 text-center bg-[#F9F8F4]/40">
                    <p className="text-xs text-[#5A6577]">No primary sources generated yet for this unit.</p>
                    <Button variant="outline" size="sm" className="mt-2 text-xs border-[#14213D]/30 text-[#14213D]" onClick={() => navigate(`/primary-sources/${unit.id}`)}>
                      Generate Sources
                    </Button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </div>
    </AppShell>
  );
}
